import type { ClientMessage, FeaturedSkill } from '@harness/shared/protocol';
import { useRef, useState } from 'react';
import styles from '../styles/composer.module.css';
import { TEXT, uploadErrorText } from '../texts';
import type { Uploads } from '../uploads';
import { PaperclipIcon, SendIcon } from './icons';

interface ComposerProps {
  chatId: string | null;
  running: boolean;
  connected: boolean;
  featured: FeaturedSkill[];
  uploads: Uploads;
  send: (message: ClientMessage) => boolean;
  onSent: () => void;
}

export function Composer({ chatId, running, connected, featured, uploads, send, onSent }: ComposerProps): React.JSX.Element {
  const [text, setText] = useState('');
  const picker = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const uploading = uploads.items.some((item) => item.path === null && item.error === null);

  const submit = (): void => {
    const trimmed = text.trim();
    if (trimmed === '' || running || uploading) return;
    const announced = uploads.items.flatMap((item) => (item.path === null ? [] : [TEXT.newFile(item.path)]));
    const prompt = announced.length === 0 ? trimmed : `${announced.join('\n')}\n\n${trimmed}`;
    if (send({ type: 'prompt', chatId, text: prompt })) {
      uploads.take();
      setText('');
      onSent();
    }
  };

  // Puts the command in front of the draft and leaves the cursor behind it; sending stays the consultant's step.
  const pick = (name: string): void => {
    const command = `/${name} `;
    setText(command + text.replace(/^\s+/, ''));
    requestAnimationFrame(() => {
      box.current?.focus();
      box.current?.setSelectionRange(command.length, command.length);
    });
  };

  return (
    <div className={styles.composer}>
      <div className={styles.inner}>
        {featured.length > 0 && (
          <ul className={styles.commands} aria-label={TEXT.commands}>
            {featured.map((skill) => (
              <li key={skill.name}>
                <button type="button" className={styles.command} disabled={running} onClick={() => pick(skill.name)}>
                  <strong>/{skill.name}</strong>
                  {skill.hint !== null && (
                    <>
                      {' '}
                      <span>{skill.hint}</span>
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {uploads.items.length > 0 && (
          <ul className={styles.chips} aria-label="Dateien">
            {uploads.items.map((item) => (
              <li key={item.id} className={item.error === null ? styles.chip : styles.chipError}>
                <span>
                  📄 {item.path ?? item.name}
                  {item.error !== null ? ` – ${uploadErrorText(item.error)}` : item.path === null ? ` ${Math.round(item.progress * 100)} %` : ''}
                </span>
                <button type="button" aria-label={`${TEXT.removeFile}: ${item.name}`} onClick={() => uploads.remove(item.id)}>
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.row}>
          <button type="button" className={styles.attach} aria-label={TEXT.attach} title={TEXT.attach} onClick={() => picker.current?.click()}>
            <PaperclipIcon />
          </button>
          <input
            ref={picker}
            className={styles.hidden}
            type="file"
            multiple
            data-testid="file-input"
            onChange={(event) => {
              if (event.target.files !== null) uploads.add([...event.target.files]);
              event.target.value = '';
            }}
          />
          <textarea
            ref={box}
            className={styles.text}
            rows={Math.min(10, Math.max(1, text.split('\n').length))}
            value={text}
            placeholder={TEXT.placeholder}
            aria-label="Nachricht"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                submit();
              }
            }}
          />
          {running ? (
            <button type="button" className={styles.stop} disabled={chatId === null || !connected} onClick={() => chatId !== null && send({ type: 'stop', chatId })}>
              ■ {TEXT.stop}
            </button>
          ) : (
            <button type="button" className={styles.send} disabled={text.trim() === '' || !connected || uploading} onClick={submit}>
              <SendIcon />
              {TEXT.send}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
